import { useRef, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Check,
  CreditCard,
  Infinity as InfinityIcon,
  Loader2,
  Sparkles,
  FileText,
  Download,
} from 'lucide-react'
import { api, extractApiError } from '../../lib/api'
import { env } from '../../lib/env'
import { Breadcrumb } from '../../components/Breadcrumb'
import { FloatingXScrollbar } from '../../components/table/FloatingXScrollbar'
import { CardCarousel } from '../../components/ui/CardCarousel'
import { PlanCheckoutModal } from '../../components/PlanCheckoutModal'
import { useAuthStore } from '../../stores/authStore'
import { invoicesResponse } from '../../lib/types'
import { downloadInvoicePdf } from '../../lib/invoice-pdf'
import { formatDateTime, formatMoney, formatNumber } from '../../lib/format'

/* The card layout in this file intentionally mirrors the Plans admin
 * page (`pages/platform/PlansPage.jsx`) — centred name + accent
 * underline + price block + boxed cap rows + green-tick features
 * list + bottom CTA. The helpers below (`fmtCap`, `CapValue`,
 * `PriceBlock`, `CapRow`) are local copies of the equivalents in
 * PlansPage. They were copied (not imported / extracted) because:
 *
 *   1. The platform PlansPage lives in a route bundle Company Admins
 *      never load, so importing across that boundary would pull
 *      unrelated admin code into the customer Billing route.
 *   2. The helpers are small and unlikely to drift — the visual
 *      contract is the entire point of mirroring.
 *
 * If a third surface ever needs the same card, extract these into
 * `components/PlanCard.jsx` and import from both pages. */

/** Left-aligned price headline matching the pricing-page reference:
 *  a large bold value with a small inline suffix ("per user / month",
 *  "tailored to your org", "N-day trial"). */
function PriceBlock({
  type,
  monthlyPrice,
  yearlyPrice,
  cadence = 'monthly',
  currency,
  trialDays,
}) {
  const big =
    'font-display text-3xl font-bold tracking-tight text-[color:var(--color-ink-strong)] leading-none'
  const suffixClass = 'text-sm text-[color:var(--color-ink-muted)]'

  if (type === 'trial') {
    return (
      <div className="flex items-baseline gap-1.5">
        <span className={big}>Free</span>
        <span className={suffixClass}>
          {trialDays ? `${trialDays}-day trial` : 'trial'}
        </span>
      </div>
    )
  }

  const toNum = (v) =>
    v === null || v === undefined || v === '' ? null : Number(v)
  const fmt = (n) => formatMoney(n, currency, { decimals: Number.isInteger(n) ? 0 : 2 })

  const m = toNum(monthlyPrice)
  const y = toNum(yearlyPrice)
  const hasM = m !== null && !Number.isNaN(m)
  const hasY = y !== null && !Number.isNaN(y) && y > 0

  // Show the SELECTED cadence's price, falling back to the other when the
  // selected one isn't offered (a yearly-only plan under the Monthly toggle
  // still shows its yearly price, and vice versa).
  const showYearly = cadence === 'yearly' ? hasY : !hasM && hasY
  if (showYearly) {
    return (
      <div className="flex items-baseline gap-1.5">
        <span className={`${big} tabular-nums`}>{fmt(y)}</span>
        <span className={suffixClass}>per year</span>
      </div>
    )
  }
  if (hasM) {
    return (
      <div className="flex items-baseline gap-1.5">
        <span className={`${big} tabular-nums`}>{fmt(m)}</span>
        <span className={suffixClass}>
          {m === 0 ? 'forever free' : 'per month'}
        </span>
      </div>
    )
  }

  return (
    <div className="flex items-baseline gap-1.5">
      <span className={big}>Custom</span>
      <span className={suffixClass}>tailored to your org</span>
    </div>
  )
}

/** Compact "unlimited-aware" cap value — ∞ glyph for null, formatted
 *  number otherwise. */
function fmtCap(n) {
  if (n === null || n === undefined)
    return <InfinityIcon size={14} aria-label="Unlimited" />
  return (
    <span className="tabular-nums">{formatNumber(Number(n))}</span>
  )
}

/** Boxed resource caps — tinted sub-card, right-aligned values. Mirrors
 *  the Sign-up + Plans cards so the three pricing surfaces match. */
function CapBox({ plan }) {
  return (
    <div className="mt-5 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] p-4 space-y-2.5">
      <CapRow label="Max Users" value={plan.maxUsers} />
      <CapRow label="Max Workspaces" value={plan.maxWorkspaces} />
      <CapRow label="Storage" value={plan.storageGb} suffix="GB" />
      <CapRow label="AI Tokens" value={plan.aiTokensPerMonth} />
    </div>
  )
}

function CapRow({ label, value, suffix }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-sm text-[color:var(--color-ink-muted)]">
        {label}
      </span>
      <span className="inline-flex items-baseline gap-1 text-sm font-semibold text-[color:var(--color-ink-strong)]">
        {fmtCap(value)}
        {suffix && value !== null && value !== undefined ? (
          <span className="text-xs font-normal text-[color:var(--color-ink-muted)]">
            {suffix}
          </span>
        ) : null}
      </span>
    </div>
  )
}

/** Canonical display order for the Billing page: Free → Starter →
 *  Growth → Enterprise (Custom). Any other paid tier slots in just
 *  before Enterprise. Matches on the plan code/name keywords so a
 *  rename in the Plans admin doesn't reshuffle the row. */
function planRank(p) {
  const key = `${p.code ?? ''} ${p.name ?? ''}`.toLowerCase()
  const monthly = Number(p.monthlyPrice)
  if (p.type === 'custom' || key.includes('enterprise')) return 3
  if (p.type === 'trial' || p.type === 'free' || monthly === 0 || key.includes('free')) return 0
  if (key.includes('starter')) return 1
  if (key.includes('growth')) return 2
  return 2.5 // any other paid tier sits between Growth and Enterprise
}

/** Returns the plans in the required left-to-right order, guaranteeing
 *  an Enterprise (Custom) card at the end. If the catalogue doesn't
 *  include an enterprise/custom plan, a synthetic "Contact sales" card
 *  is appended so the page always shows the full 4-tier ladder. */
function orderPlansForBilling(plans) {
  const ordered = [...plans].sort((a, b) => planRank(a) - planRank(b))
  const hasEnterprise = ordered.some(
    (p) =>
      p.type === 'custom' ||
      // A Contact-sales plan IS the enterprise/custom tier, whatever it's
      // named — so a misspelling like "Enterpise" can't slip past the name
      // check and trigger a duplicate synthetic card.
      p.isContactSales === true ||
      `${p.code ?? ''} ${p.name ?? ''}`.toLowerCase().includes('enterprise'),
  )
  if (!hasEnterprise) {
    ordered.push({
      code: 'enterprise',
      name: 'Enterprise',
      type: 'custom',
      monthlyPrice: null,
      currency: 'USD',
      description:
        'For large teams that need custom limits, security, and support.',
      maxUsers: null,
      maxWorkspaces: null,
      storageGb: null,
      aiTokensPerMonth: null,
      featuresList: [
        'Everything in Growth',
        'Unlimited users & workspaces',
        'Custom AI token allowance',
        'SSO & advanced security',
        'Dedicated support & SLA',
      ],
      __synthetic: true,
    })
  }
  return ordered
}

export function BillingPage() {
  // The paid plan the customer is checking out — drives the checkout
  // dialog (billing cycle + coupon + pricing summary + Pay). Null = closed.
  const [checkoutPlan, setCheckoutPlan] = useState(null)
  // Page-level billing-cycle toggle: switches every card's headline price
  // and seeds the checkout dialog's default cycle. Only shown when at least
  // one plan carries a yearly price.
  const [cadence, setCadence] = useState('monthly')
  const tenant = useAuthStore((s) => s.tenant)
  const refreshUser = useAuthStore((s) => s.refreshUser)
  // The tenant's currently-active plan code (e.g. "starter", "growth").
  // Sourced from the same `tenant.billing` block AdminLayout uses for
  // its billing-lock overlay, so the "Current Plan" indicator on this
  // page stays in lockstep with the sidebar's lock state.
  const currentPlanCode = tenant?.billing?.planCode ?? null
  // Has the tenant actually SELECTED / activated a plan yet? At signup the
  // default (Free) plan is stamped as `billing.planCode` while `planSelectedAt`
  // stays null and the PlanGate blocks the app until it's set. Until a plan is
  // selected, NO card is treated as "current" — otherwise the Free card would
  // render as an un-clickable "Current Plan" and hide the "Continue with Free"
  // action (POST /payments/activate-free) that activates it, trapping the user
  // on /billing (the Free / PlanGate dead-end).
  const hasSelectedPlan = Boolean(tenant?.planSelectedAt)

  const plansQuery = useQuery({
    queryKey: ['billing-plans'],
    queryFn: async () => {
      const res = await api.get('/plans/public')
      return Array.isArray(res.data) ? res.data : (res.data?.data ?? [])
    },
  })

  const activateFreeMutation = useMutation({
    mutationFn: async (planCode) => {
      const res = await api.post('/payments/activate-free', { planCode })
      return res.data
    },
    onSuccess: async () => {
      toast.success('Free Trial activated.')
      await refreshUser()
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const plans = plansQuery.data ?? []
  // Fixed left-to-right order: Free, Starter, Growth, Enterprise (Custom),
  // with an Enterprise card guaranteed even if the catalogue omits it.
  const orderedPlans = orderPlansForBilling(plans)
  // Only surface the Monthly/Yearly toggle when a yearly price exists to
  // switch to — otherwise it's a no-op control.
  const anyYearly = plans.some(
    (p) => p.yearlyPrice != null && p.yearlyPrice !== '' && Number(p.yearlyPrice) > 0,
  )

  return (
    <div>
      <Breadcrumb current="Billing" />

      <div className="mb-6">
        <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
          <CreditCard
            size={20}
            className="text-[color:var(--color-accent-strong)]"
          />
          Billing &amp; Subscription
        </h1>
        <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
          Pick a plan — you can apply a coupon and review the total before
          you pay.
        </p>
      </div>

      {anyYearly && !plansQuery.isLoading && !plansQuery.isError ? (
        <div className="mb-6 inline-flex rounded-lg border border-[color:var(--color-rule)] p-0.5 bg-[color:var(--color-canvas)]">
          {['monthly', 'yearly'].map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCadence(c)}
              className={
                'px-4 py-1.5 rounded-md text-sm font-medium capitalize transition-colors cursor-pointer ' +
                (cadence === c
                  ? 'bg-[color:var(--color-card)] text-[color:var(--color-ink-strong)] shadow-sm'
                  : 'text-[color:var(--color-ink-muted)]')
              }
            >
              {c}
            </button>
          ))}
        </div>
      ) : null}

      {plansQuery.isLoading ? (
        <p className="text-sm text-[color:var(--color-ink-muted)] inline-flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading plans…
        </p>
      ) : plansQuery.isError ? (
        <p className="text-sm text-[color:var(--color-priority-urgent)]">
          {extractApiError(plansQuery.error).message}
        </p>
      ) : plans.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-12 text-center">
          <Sparkles
            size={26}
            className="mx-auto mb-2 text-[color:var(--color-ink-subtle)]"
          />
          <p className="text-sm font-semibold text-[color:var(--color-ink-strong)]">
            No plans are available right now
          </p>
        </div>
      ) : (
        // Plans in a horizontal snap-scrolling carousel (1 / 2 / 3-up
        // responsive), matching the master admin Plans page. Prev/next
        // arrows + dot indicators appear only when the cards overflow the
        // row, so a handful of plans still reads as a plain left-aligned
        // row. Each card is forced to the row's tallest height by the
        // carousel slot (`[&>*]:h-full`).
        <CardCarousel ariaLabel="Plans">
          {orderedPlans.map((plan) => {
            // A plan is payable on EITHER cadence — a plan priced only
            // yearly (blank monthly) is still a real paid plan, not a
            // "Custom / Contact sales" tier. The card reads both prices;
            // PriceBlock falls back to the yearly figure when there's no
            // monthly one, and the checkout dialog defaults to whichever
            // cadence the plan is priced in.
            const toNum = (v) =>
              v === null || v === undefined || v === '' ? null : Number(v)
            const monthly = toNum(plan.monthlyPrice)
            const yearly = toNum(plan.yearlyPrice)
            const hasMonthly =
              monthly !== null && !Number.isNaN(monthly) && monthly > 0
            const hasYearly =
              yearly !== null && !Number.isNaN(yearly) && yearly > 0
            const isPaid = hasMonthly || hasYearly
            const isFree =
              monthly === 0 || plan.type === 'trial' || plan.type === 'free'
            const isFeatured = Boolean(plan.isDefault)
            // Match on plan.code (the stable slug, e.g. "starter") so a
            // rename in Plans admin doesn't drop the "Current Plan"
            // indicator on the customer's existing subscription.
            const isCurrent =
              hasSelectedPlan &&
              currentPlanCode !== null &&
              plan.code === currentPlanCode

            // CTA copy: "Get {planName}". For the user's current plan it stays
            // "Current Plan", un-clickable. During onboarding (no plan selected
            // yet) the Free card reads "Continue with Free" and activates it.
            const ctaLabel = isCurrent ? 'Current Plan' : `Get ${plan.name}`
            const freeCtaLabel = hasSelectedPlan ? ctaLabel : 'Continue with Free'

            return (
              <div
                key={plan.code}
                className={
                  'group relative flex flex-col rounded-2xl bg-[color:var(--color-card)] p-6 transition-all hover:-translate-y-0.5 hover:shadow-md ' +
                  (isCurrent
                    ? 'border border-[color:var(--color-success)] shadow-sm'
                    : isFeatured
                      ? 'border border-[color:var(--color-accent-strong)] shadow-sm'
                      : 'border border-[color:var(--color-rule)]')
                }
              >
                {/* Status ribbon — straddles the card's top border,
                 *  centered. Current Plan wins over Most Popular. */}
                {isCurrent ? (
                  <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full bg-[color:var(--color-success)] px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider text-[color:var(--color-on-success)] shadow-sm whitespace-nowrap">
                    <Check size={12} strokeWidth={3} />
                    Current Plan
                  </span>
                ) : plan.isPopular ? (
                  <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full bg-[color:var(--color-accent-strong)] px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider text-white shadow-sm whitespace-nowrap">
                    <Sparkles size={12} strokeWidth={2.5} />
                    Most Popular
                  </span>
                ) : null}

                <h3 className="font-display text-xl font-semibold tracking-tight text-[color:var(--color-ink-strong)]">
                  {plan.name}
                </h3>

                {/* Price — large value + small inline period suffix. */}
                <div className="mt-3">
                  <PriceBlock
                    type={plan.type}
                    monthlyPrice={plan.monthlyPrice}
                    yearlyPrice={plan.yearlyPrice}
                    cadence={cadence}
                    currency={plan.currency}
                    trialDays={plan.trialDays}
                  />
                </div>

                {plan.description ? (
                  <p className="mt-3 text-sm text-[color:var(--color-ink-muted)] leading-snug">
                    {plan.description}
                  </p>
                ) : null}

                <CapBox plan={plan} />

                {/* CTA — four states (current / paid / free / custom).
                 *  Paid keeps the brand primary (the key money action);
                 *  the rest are outlined to match the reference. */}
                <div className="mt-5">
                  {isCurrent ? (
                    <div className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold border border-[color:var(--color-success)] text-[color:var(--color-success)] bg-[color:var(--color-success-bg)] cursor-default">
                      <Check size={14} strokeWidth={2.5} />
                      {ctaLabel}
                    </div>
                  ) : isPaid ? (
                    <button
                      type="button"
                      onClick={() => setCheckoutPlan(plan)}
                      className="taskmagix-btn-primary w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold cursor-pointer transition-all"
                    >
                      {ctaLabel}
                    </button>
                  ) : isFree ? (
                    <button
                      type="button"
                      onClick={() => activateFreeMutation.mutate(plan.code)}
                      disabled={activateFreeMutation.isPending}
                      className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold border border-[color:var(--color-rule)] text-[color:var(--color-ink-strong)] hover:border-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-canvas)] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                    >
                      {activateFreeMutation.isPending ? (
                        <>
                          <Loader2 size={14} className="animate-spin" />
                          Activating…
                        </>
                      ) : (
                        freeCtaLabel
                      )}
                    </button>
                  ) : (
                    <a
                      href={`${env.websiteUrl}/contact-us`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full inline-flex items-center justify-center px-4 py-2.5 rounded-xl text-sm font-medium text-center border border-[color:var(--color-rule)] text-[color:var(--color-ink)] hover:border-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-canvas)] cursor-pointer transition-colors"
                    >
                      Contact sales
                    </a>
                  )}
                </div>

                {/* Feature ticks — the plan's marketed features. */}
                {plan.featuresList && plan.featuresList.length > 0 ? (
                  <ul className="mt-5 space-y-2.5 text-sm">
                    {plan.featuresList.map((f, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Check
                          size={15}
                          strokeWidth={2.5}
                          className="text-[color:var(--color-success)] shrink-0 mt-0.5"
                        />
                        <span className="text-[color:var(--color-ink)] leading-snug">
                          {f}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            )
          })}
        </CardCarousel>
      )}

      <p className="mt-5 text-xs text-[color:var(--color-ink-subtle)]">
        Payments are processed securely by Razorpay. Your subscription
        activates as soon as the payment is confirmed.
      </p>

      <PlanCheckoutModal
        // Remount per plan so cadence/coupon state resets to that plan's
        // defaults (e.g. a yearly-only plan opens on the Yearly cycle).
        key={checkoutPlan?.code ?? 'none'}
        plan={checkoutPlan}
        open={Boolean(checkoutPlan)}
        defaultCadence={cadence}
        onClose={() => setCheckoutPlan(null)}
        onPaid={refreshUser}
      />

      <BillingInvoices />
    </div>
  )
}

/** Company Admin's own invoices — one row per completed payment, with a
 *  client-side PDF download. Hidden entirely until the first invoice
 *  exists so it never shows an empty table on a fresh account. */
function BillingInvoices() {
  // Scroll container for the invoices table — drives the floating
  // horizontal scrollbar pinned to the viewport bottom.
  const invoiceScrollRef = useRef(null)
  const [page, setPage] = useState(1)
  const q = useQuery({
    queryKey: ['billing-invoices', page],
    queryFn: async () => {
      const res = await api.get('/billing/invoices', { params: { page, limit: 10 } })
      return invoicesResponse.parse(res.data)
    },
    placeholderData: (prev) => prev,
  })
  const rows = q.data?.invoices ?? []
  const info = q.data?.pagination ?? null

  if (!q.isLoading && rows.length === 0 && page === 1) return null

  const money = (n, c = 'INR') => formatMoney(Number(n ?? 0), c)

  return (
    <div className="mt-10">
      <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] mb-3 flex items-center gap-2">
        <FileText size={18} className="text-[color:var(--color-accent-strong)]" />
        Invoices
      </h2>
      <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] overflow-hidden">
        {/* `data-fd-xscroll` opts this table into the floating horizontal
         *  scrollbar below — the same affordance DataTable gives the other
         *  admin tables. Purely additive: no layout or sizing change. */}
        <div ref={invoiceScrollRef} data-fd-xscroll className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border-b border-[color:var(--color-rule)]">
              <tr>
                <th className="px-4 py-3 font-medium">Invoice No.</th>
                <th className="px-4 py-3 font-medium">Plan</th>
                <th className="px-4 py-3 font-medium">Amount</th>
                <th className="px-4 py-3 font-medium">Issued</th>
                {/* Singular — the row has one control (Download PDF). */}
                <th className="px-4 py-3 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((inv) => (
                <tr key={inv._id} className="border-b border-[color:var(--color-rule)] last:border-b-0 hover:bg-[color:var(--color-card-hover)]">
                  <td className="px-4 py-3 font-medium text-[color:var(--color-ink-strong)] tabular-nums whitespace-nowrap">{inv.invoiceNumber}</td>
                  <td className="px-4 py-3 text-[color:var(--color-ink-muted)]">{inv.planName || inv.planCode || '—'}</td>
                  <td className="px-4 py-3 tabular-nums text-[color:var(--color-ink)] whitespace-nowrap">{money(inv.total ?? inv.amount, inv.currency)}</td>
                  <td className="px-4 py-3 text-[color:var(--color-ink-muted)] whitespace-nowrap">{formatDateTime(inv.issuedAt)}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => downloadInvoicePdf(inv)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-[color:var(--color-rule)] text-xs text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)]"
                    >
                      <Download size={13} /> PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {info && info.totalPages > 1 ? (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[color:var(--color-rule)] text-sm text-[color:var(--color-ink-muted)]">
            <span className="tabular-nums">Page {info.page} of {info.totalPages}</span>
            <div className="flex items-center gap-2">
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="px-3 py-1.5 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] disabled:opacity-40 hover:bg-[color:var(--color-card-hover)]">Previous</button>
              <button type="button" disabled={page >= info.totalPages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1.5 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] disabled:opacity-40 hover:bg-[color:var(--color-card-hover)]">Next</button>
            </div>
          </div>
        ) : null}
      </div>
      <FloatingXScrollbar containerRef={invoiceScrollRef} />
    </div>
  )
}
